import React, { useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'
import { Show } from '@app/components/util'

import { patchContractor, getContractors } from '@app/api/routes/contractors'

import { CONTRACTORS_KEY } from '@app/constants/queryKeys'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { LntButton } from '@app/components/base'
import { ExtendedTable, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { CommonContractorDrawer } from '@app/drawers/CommonContractorDrawer'

import { TopFilters } from './ui'
import { getColumns, adaptContractorsTableData } from './lib'
import { topFiltersQuerySchema, columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { Key } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { GetLocationContractorsParams, CommonContractorsData } from '@app/api/routes/contractors/types'
import type { PaginatedParams } from '@app/types/pagination'

const columns = getColumns()

export const ContractorsTab: React.FC = () => {
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const {
    value: isOpenNewLinkContractorsModal,
    setUntoggled: handleCloseNewLinkContractorsModal,
    setToggled: handleOpenNewLinkContractorsModal
  } = useToggle()

  const queryClient = useQueryClient()

  const connectionsContractorsTabParams = useMemo<PaginatedParams<GetLocationContractorsParams>>(
    () => ({
      ...(queryFilters as GetLocationContractorsParams),
      limit: paginationParams.limit,
      offset: paginationParams.offset
    }),
    [paginationParams, queryFilters]
  )

  const { data: contractorsTableData, isLoading: isListsDataLoading } = useQuery({
    queryKey: [CONTRACTORS_KEY.contractorsItems, connectionsContractorsTabParams],
    queryFn: async () => await getContractors(connectionsContractorsTabParams),
    select: contractorsData => adaptContractorsTableData(contractorsData)
  })

  const patchContractorMutation = useMutation({
    mutationFn: patchContractor,
    onSuccess: async () => await queryClient.invalidateQueries({ queryKey: [CONTRACTORS_KEY.contractorsItems] })
  })

  const [selectedIds, setSelectedIds] = useState<Key[]>([])

  const firstSelectedContractor = useMemo(
    () => contractorsTableData?.dataSource?.find(item => item.key === selectedIds[0]),
    [selectedIds]
  )

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(key => key))
  }

  const handleEditContractor = (data: CommonContractorsData): void => {
    const id = Number(selectedIds?.[0])

    if (!id || !data) return

    patchContractorMutation.mutate({ id, data }, { onSettled: handleCloseNewLinkContractorsModal })
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.contractorsTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredContractorsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: contractorsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = [
    <Show
      key='view_change_copy-show'
      condition={ selectedIds.length === 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
      >
        Удалить
      </LntButton>

      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleOpenNewLinkContractorsModal }
      >
        Редактировать
      </LntButton>

      <SubheaderToolbarDivider />
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <ContentLayout>
      <TopFilters
        onSearch={ handleFiltersChange }
        initialFilters={ queryFilters as FiltersParams }
      />

      <ExtendedTable
        loading={ isListsDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredContractorsDataSource }
        toolbar={ toolbar }
        rowSelection={ {
          onChange: handleSelectedRowChange
        } }
        pagination={ {
          ...pagination,
          total: contractorsTableData?.count,
          onChange: handlePaginationChange
        } }
      />

      <CommonContractorDrawer
        title='Редактирование подрядчика'
        open={ isOpenNewLinkContractorsModal }
        onClose={ handleCloseNewLinkContractorsModal }
        onSubmit={ handleEditContractor }
        data={ firstSelectedContractor }
      />
    </ContentLayout>
  )
}
